import {
  type Extraction,
  type Gray,
  JPEG_QUALITY,
  MAX_EDGE,
  type Need,
  SHARP_MIN,
  sharpness,
  toGray,
} from "@larder/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { extractLabel } from "../data/extract";

/**
 * Manual capture: one shot, when the user asks for it.
 *
 * This replaces the automatic sweep. The sweep fired every couple of seconds
 * and decided for itself what it was looking at, which produced the failure
 * reported from the field — the name would land, then a date shot would be read
 * as a different product and reset it. Watching a value you just captured
 * disappear is worse than a slower flow.
 *
 * The blur gate survives, but demoted from trigger to ADVICE: it now drives a
 * live "hold steady" indicator instead of silently deciding when to spend
 * money. The user presses the button; we tell them whether it is worth pressing.
 */

/** How often the preview's sharpness is sampled. Local work only. */
const TICK_MS = 200;
const TINY = 48;

export interface CaptureApi {
  /** Live blur reading, 0-1. Above ~0.5 is comfortably readable. */
  focus: number;
  steady: boolean;
  busy: boolean;
  error: string | null;
  shoot: (need: Need, context: unknown) => Promise<Extraction | null>;
  clearError: () => void;
}

export function useCapture(
  video: React.RefObject<HTMLVideoElement | null>,
  timezone: string,
): CaptureApi {
  const [focus, setFocus] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiny = useRef<HTMLCanvasElement | null>(null);
  const full = useRef<HTMLCanvasElement | null>(null);

  const grabGray = useCallback((): Gray | null => {
    const v = video.current;
    if (!v?.videoWidth) return null;
    if (!tiny.current) {
      tiny.current = document.createElement("canvas");
      tiny.current.width = TINY;
      tiny.current.height = TINY;
    }
    const ctx = tiny.current.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, TINY, TINY);
    return toGray(ctx.getImageData(0, 0, TINY, TINY).data, TINY, TINY);
  }, [video]);

  // Live focus meter. Cheap, and the only thing running between shots.
  useEffect(() => {
    const id = setInterval(() => {
      const frame = grabGray();
      if (!frame) return;
      // Normalised against the threshold that was tuned on real packaging, so
      // "half full" means "about good enough".
      setFocus(Math.min(1, sharpness(frame) / (SHARP_MIN * 2)));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [grabGray]);

  const shoot = useCallback(
    async (need: Need, context: unknown): Promise<Extraction | null> => {
      const v = video.current;
      if (!v?.videoWidth || busy) return null;

      if (!full.current) full.current = document.createElement("canvas");
      // NEVER below MAX_EDGE. At 768px the model returns the manufacture date
      // as a printed expiry — cheaper images produce WRONG dates, not missing
      // ones. See docs/decisions/0004-image-resolution.md.
      const scale = Math.min(1, MAX_EDGE / Math.max(v.videoWidth, v.videoHeight));
      full.current.width = Math.round(v.videoWidth * scale);
      full.current.height = Math.round(v.videoHeight * scale);
      const ctx = full.current.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(v, 0, 0, full.current.width, full.current.height);
      const image = full.current.toDataURL("image/jpeg", JPEG_QUALITY).split(",")[1];
      if (!image) return null;

      setBusy(true);
      setError(null);
      try {
        const out = await extractLabel(image, need, context, timezone);
        return out.result;
      } catch (e) {
        setError(readable(e));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [video, timezone, busy],
  );

  return {
    focus,
    steady: focus >= 0.5,
    busy,
    error,
    shoot,
    clearError: useCallback(() => setError(null), []),
  };
}

/** Callable errors arrive with a code; the message alone is often useless. */
function readable(e: unknown): string {
  const err = e as { code?: string; message?: string };
  if (err.code === "functions/resource-exhausted") {
    return "That's the most scanning Larder does in a day. Back tomorrow — typing always works.";
  }
  if (err.code === "functions/permission-denied") {
    return "Something's off with this account. Try signing out and back in.";
  }
  if (err.code === "functions/failed-precondition") {
    return "Scanning is having a moment. Typing still works.";
  }
  if (err.code === "functions/unauthenticated") return "Signed out — sign in again.";
  return err.message ?? "Couldn't read that. Try again, or type it in.";
}
